import { Component, computed, inject, input } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';

import { ForecastVersionResponse } from '@api-response-types/forecast-version.response';
import {
  DateRange,
  FormFieldComponent,
  FormInputDateRangeComponent,
  FormInputSelectComponent,
  PmButtonComponent,
} from '@libs/ui';
import { formatValueAsDate } from '@core/utils/formatter-utility';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { selectIsLoggedInUserAdmin } from 'core/store/auth/auth.selectors';
import { ReportingStateService } from 'demand-forecasting/reporting/services/reporting-state.service';
import { format } from 'date-fns';
import { LucideDatabaseBackup } from '@lucide/angular';
import { ReportingImportDialogComponent } from '../import-dialog/reporting-import-dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'pm-reporting-toolbar',
  templateUrl: './reporting-toolbar.component.html',
  host: { class: 'flex flex-col gap-4' },
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    FormFieldComponent,
    FormInputSelectComponent,
    PmButtonComponent,
    FormInputDateRangeComponent,
  ],
})
export class ReportingToolbarComponent {
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);
  protected readonly reportingState = inject(ReportingStateService);
  protected readonly isAdmin = this.store.selectSignal(
    selectIsLoggedInUserAdmin,
  );

  protected readonly importIcon = LucideDatabaseBackup;

  reportingReadyVersions = input.required<ForecastVersionResponse[]>();
  importableVersions = input<ForecastVersionResponse[]>([]);
  additionalTimeSeriesNames = input<string[]>([]);

  protected dateRange = new FormControl<DateRange | undefined>(undefined);

  constructor() {
    this.dateRange.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) => {
        const newMin = value?.start
          ? format(value.start, 'yyyy-MM-dd')
          : undefined;
        const newMax = value?.end ? format(value.end, 'yyyy-MM-dd') : undefined;
        if (newMin !== this.reportingState.minDate())
          this.reportingState.minDate.set(newMin);
        if (newMax !== this.reportingState.maxDate())
          this.reportingState.maxDate.set(newMax);
      });
  }

  protected readonly versionOptions = computed(() =>
    this.reportingReadyVersions().map((v) => ({
      value: v.id,
      label: this.formatVersionLabel(v),
    })),
  );

  protected readonly timeSeriesOptions = computed(() =>
    this.additionalTimeSeriesNames().map((ts) => ({
      value: ts,
      label: ts,
    })),
  );

  protected onVersionSelectionChange(
    selectedIds: string | (string | number)[],
  ): void {
    const ids = Array.isArray(selectedIds)
      ? (selectedIds as string[])
      : [selectedIds];
    this.reportingState.selectedVersionIds.set(ids);
  }

  protected onTimeSeriesSelectionChange(
    selected: string | (string | number)[],
  ): void {
    const values = Array.isArray(selected)
      ? (selected as string[])
      : [selected];
    this.reportingState.selectedTimeSeries.set(values);
  }

  protected openImportDialog(): void {
    const dialogRef = this.dialog.open(ReportingImportDialogComponent);

    dialogRef.componentInstance.importableVersions.set(
      this.importableVersions(),
    );

    dialogRef.afterClosed().subscribe((versionId: string | null) => {
      if (!versionId) return;
      this.reportingState.triggerImport(versionId).subscribe();
    });
  }

  private formatVersionLabel(version: ForecastVersionResponse): string {
    return `FC ${formatValueAsDate(version.createdAt)}`;
  }
}
